
import { useEffect, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../../api/client';
import { voicesApi, type VoiceCatalogParams } from '../../api/endpoints';
import { queryKeys, useTaskTracker, type TaskTracker } from '../../hooks';
import type {
  Page,
  SharedVoiceAddRequest,
  SharedVoiceAddResult,
  SharedVoiceSearchResult,
  ToolTask,
  VoiceCatalogEntry,
  VoiceCatalogStats,
  VoiceCatalogSyncRequest,
  VoiceLibraryParams,
} from '../../types/api';

export function useVoiceCatalog(
  params: VoiceCatalogParams = {},
): UseQueryResult<Page<VoiceCatalogEntry>, ApiError> {
  return useQuery<Page<VoiceCatalogEntry>, ApiError>({
    queryKey: [...queryKeys.voicesRoot, 'catalog', params],
    queryFn: () => voicesApi.catalog(params),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

const SYNC_SETTLE_MS = 3000;

// столько же держит бэк (_AUTOSYNC_MIN_GAP): раньше жать бессмысленно
const SYNC_COOLDOWN_MS = 60_000;

export interface CatalogRefresh {
  start: ReturnType<typeof useMutation<Page<VoiceCatalogEntry>, ApiError, VoiceCatalogParams>>;
  cooling: boolean;
}

export function useRefreshVoiceCatalog(lastSyncedAt?: string | null): CatalogRefresh {
  const qc = useQueryClient();
  const [cooling, setCooling] = useState(false);

  // Время синка приходит с бэка, поэтому пауза переживает и смену вкладки, и
  // перезагрузку страницы: иначе кнопка выглядела доступной, пока сервер её игнорит.
  const syncedAgo = lastSyncedAt ? Date.now() - new Date(lastSyncedAt).getTime() : Infinity;
  const serverCooling = syncedAgo < SYNC_COOLDOWN_MS;

  const start = useMutation<Page<VoiceCatalogEntry>, ApiError, VoiceCatalogParams>({
    // с refresh=true бэк только запускает обход аккаунта, а отвечает прежними
    // строками: перечитывать каталог сразу же смысла нет, ждём обход
    mutationFn: async (params) => {
      const page = await voicesApi.catalog({ ...params, refresh: true });
      await new Promise((resolve) => window.setTimeout(resolve, SYNC_SETTLE_MS));
      return page;
    },
    retry: false,
    onMutate: () => {
      setCooling(true);
    },
    // the sync also evicts and restores profiles, so the profile lists go stale with the catalog
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.voicesRoot });
    },
  });

  useEffect(() => {
    if (!cooling) return;
    const timer = window.setTimeout(() => setCooling(false), SYNC_COOLDOWN_MS);
    return () => window.clearTimeout(timer);
  }, [cooling]);

  return { start, cooling: cooling || serverCooling };
}

export function useVoiceCatalogStats(
  provider?: string,
): UseQueryResult<VoiceCatalogStats, ApiError> {
  return useQuery<VoiceCatalogStats, ApiError>({
    queryKey: [...queryKeys.voicesRoot, 'catalog', 'stats', provider ?? null],
    queryFn: () => voicesApi.catalogStats(provider),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export type VoiceLibraryFilters = Omit<VoiceLibraryParams, 'page'>;

// у провайдера 15к голосов, без потолка «Показать ещё» выкачает всё в память
const LIBRARY_MAX_PAGES = 100;

export function useVoiceLibrary(
  params: VoiceLibraryFilters,
  enabled: boolean,
): UseInfiniteQueryResult<InfiniteData<SharedVoiceSearchResult, number>, ApiError> {
  return useInfiniteQuery<
    SharedVoiceSearchResult,
    ApiError,
    InfiniteData<SharedVoiceSearchResult, number>,
    readonly unknown[],
    number
  >({
    queryKey: [...queryKeys.voicesRoot, 'library', params],
    queryFn: ({ pageParam }) => voicesApi.library({ ...params, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.has_more && pages.length < LIBRARY_MAX_PAGES ? pages.length : undefined,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useAddVoiceFromLibrary() {
  const qc = useQueryClient();
  return useMutation<SharedVoiceAddResult, ApiError, SharedVoiceAddRequest>({
    mutationFn: (payload) => voicesApi.libraryAdd(payload),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.voicesRoot });
    },
  });
}

export interface CatalogSync {
  start: ReturnType<typeof useMutation<ToolTask, ApiError, VoiceCatalogSyncRequest>>;
  task: TaskTracker;
}

export function useSyncVoiceCatalog(): CatalogSync {
  const qc = useQueryClient();
  const task = useTaskTracker([queryKeys.voicesRoot, queryKeys.charactersRoot]);
  const start = useMutation<ToolTask, ApiError, VoiceCatalogSyncRequest>({
    mutationFn: (payload) => voicesApi.catalogSync(payload),
    retry: false,
    onSuccess: (accepted) => {
      task.track(accepted.id);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
  });
  return { start, task };
}
